<template>
    <div>
        <Transition name="fade">
            <div
                v-if="bannerModel"
                ref="languageHint"
                class="hidden lg:block absolute inset-x-0 text-right bg-black-50 text-black-900 py-4 px-10 xl:px-20"
                :class="subsiteNavExists ? 'top-[calc(100%_+_3.5rem)]' : 'top-full'">
                <span>{{ hint }}</span>
            </div>
        </Transition>

        <Transition name="fade">
            <slot v-if="showLanguageBanner" name="mobile-language-banner" :subsite-nav-exists="subsiteNavExists" />
        </Transition>

        <Transition name="fade">
            <div
                v-if="noContentModel"
                class="fixed left-0 top-0 w-full h-full bg-black-900 bg-opacity-30">
                <with-top-margin-content class="container" :sm="4" :md="6" :lg="8" :xl="8">
                    <div class="relative xl:w-2/3 mx-auto bg-primary-50 py-6 pl-8 pr-18">
                        <div
                            class="absolute top-0 right-0 flex justify-center items-center w-10 h-10 bg-primary-500 hover:bg-black-900 cursor-pointer"
                            @click="noContentModel = false">
                            <Icon name="close" class="w-4 h-4 text-white stroke-current"/>
                        </div>
                        <span class="text-para-s text-black-900">{{ noContentText }}</span>
                    </div>
                </with-top-margin-content>
            </div>
        </Transition>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import Cookies from 'js-cookie';
import { useNavigationStore } from '../../stores/navigation';
import { mapWritableState } from 'pinia';
import Icon from '../base/Icon.vue';
import WithTopMarginContent from "../base/WithTopMarginContent.vue";

export default {
    components: { WithTopMarginContent, Icon },
    inject: ['$lang'],
    props: {
        showLocaleHints: { type: Boolean, default: false },
        showLanguageBanner: { type: Boolean, default: false },
        availableLocales: { type: Array as PropType<Array<string>>, default: () => [] },
        hint: { type: String },
        noContentTexts: { type: Object },
    },
    data() {
        return {
            bannerModel: false,
            noContentModel: false,
            prevSelectedLocale: undefined,
        };
    },
    computed: {
        ...mapWritableState(useNavigationStore, [
            'adaptiveLanguageSwitcherOpened', 'subsiteNavExists'
        ]),
        noContentText(): string {
            const texts: Map<string, string> = new Map(Object.entries(this.noContentTexts));
            return this.prevSelectedLocale && texts.has(this.prevSelectedLocale)
                ? texts.get(this.prevSelectedLocale)
                : null;
        },
    },
    mounted(): void {
        this.prevSelectedLocale = Cookies.get('prev-selected-locale');
        this.adaptiveLanguageSwitcherOpened = this.showLanguageBanner;
        if (this.showLocaleHints) {
            if (this.showLanguageBanner) {
                if (!Cookies.get('shown-language-hint')) {
                    this.bannerModel = true;
                    Cookies.set('shown-language-hint', 'true');
                }
            } else {
                Cookies.remove('shown-language-hint');
            }
            if (this.showNoContentForLocale()) {
                if (!Cookies.get('shown-no-content-hint') && this.isSameDomain()) {
                    this.noContentModel = true;
                    Cookies.set('shown-no-content-hint', 'true');
                }
            } else {
                Cookies.remove('shown-no-content-hint');
            }
            document.addEventListener('click', this.onOutsideLanguageHintClick);
            Cookies.set('prev-selected-locale', this.$lang);
        } else {
            if (Cookies.get('shown-language-hint')) {
                Cookies.remove('shown-language-hint');
            }
            if (Cookies.get('shown-no-content-hint')) {
                Cookies.remove('shown-no-content-hint');
            }
        }
    },
    methods: {
        onOutsideLanguageHintClick(evt: Event) {
            if (this.bannerModel) {
                // close the language hint banner, when user clicks on the page outside of the banner
                const languageHint = this.$refs.languageHint as HTMLElement;
                const target = evt.target as HTMLElement;

                if (!languageHint.contains(target)) {
                    this.bannerModel = false;
                }
            }
        },
        isSameDomain() {
            if (!document.referrer) {
                return false;
            }
            const url = new URL(document.referrer);
            return url.origin === window.origin;
        },
        showNoContentForLocale() {
            if (this.prevSelectedLocale) {
                return !this.availableLocales.includes(this.prevSelectedLocale);
            } else {
                return false;
            }
        },
    },
};
</script>
